import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Alert,
  Paper,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminShell from '../AdminShell';
import { LoadingState, PageHeader } from '../../../ui';
import UserPaymentHistoryView from './UserPaymentHistoryView';
import UserProjectHistoryView from './UserProjectHistoryView';

const createDefaultEntitlementForm = () => {
  const now = new Date();
  const trialEnd = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

  return {
    entitlementType: 'Trial',
    status: 'Active',
    startsAtUtc: toDateTimeLocalValue(now),
    expiresAtUtc: toDateTimeLocalValue(trialEnd),
  };
};

function toDateTimeLocalValue(date) {
  const localDate = new Date(date);
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
  return localDate.toISOString().slice(0, 16);
}

function formatDate(dateValue) {
  if (!dateValue) {
    return 'N/A';
  }

  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? 'N/A' : parsedDate.toLocaleString();
}

const UserManagement = () => {
  const { email } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [entitlementForm, setEntitlementForm] = useState(createDefaultEntitlementForm());
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      try {
        setLoading(true);
        const jwt = localStorage.getItem('jwttoken');
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminuserdata/user-details/${email}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch user: ${response.status}`);
        }

        const json = await response.json();
        const fetchedUser = json.data;
        setUser(fetchedUser);

        if (fetchedUser.bamrocEntitlement) {
          setEntitlementForm({
            entitlementType: fetchedUser.bamrocEntitlement.entitlementType || 'Trial',
            status: fetchedUser.bamrocEntitlement.status || 'Active',
            startsAtUtc: toDateTimeLocalValue(fetchedUser.bamrocEntitlement.startsAtUtc),
            expiresAtUtc: toDateTimeLocalValue(fetchedUser.bamrocEntitlement.expiresAtUtc),
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [email]);

  const handleEntitlementFieldChange = (field, value) => {
    setEntitlementForm((previousForm) => ({
      ...previousForm,
      [field]: value,
    }));
  };

  const handleDeleteUser = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const jwt = localStorage.getItem('jwttoken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminuserdata/delete-user-by-id/${user.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete user.');
      }

      const json = await response.json();
      setSuccess(json.message);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleAlgorithmAccess = async (enable) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const jwt = localStorage.getItem('jwttoken');
      const endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminuserdata/${
        enable ? 'enable-algorithm-access' : 'disable-algorithm-access'
      }`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${enable ? 'enable' : 'disable'} algorithm access.`);
      }

      const json = await response.json();
      setUser((previousUser) => ({
        ...previousUser,
        algorithmAccess: enable,
      }));
      setSuccess(json.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveEntitlement = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const jwt = localStorage.getItem('jwttoken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminbamrocentitlement/assign`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            entitlementType: entitlementForm.entitlementType,
            status: entitlementForm.status,
            startsAtUtc: new Date(entitlementForm.startsAtUtc).toISOString(),
            expiresAtUtc: new Date(entitlementForm.expiresAtUtc).toISOString(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save BAMROC cloud entitlement.');
      }

      const json = await response.json();
      setUser((previousUser) => ({
        ...previousUser,
        bamrocEntitlement: json.entitlement,
      }));
      setSuccess(json.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEntitlementStatusChange = async (status) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const jwt = localStorage.getItem('jwttoken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/adminbamrocentitlement/update-status`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, status }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update status to ${status}.`);
      }

      const json = await response.json();
      setUser((previousUser) => ({
        ...previousUser,
        bamrocEntitlement: json.entitlement,
      }));
      setEntitlementForm((previousForm) => ({
        ...previousForm,
        status,
      }));
      setSuccess(json.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminShell selected="users">
        <LoadingState />
      </AdminShell>
    );
  }

  if (!user) {
    return (
      <AdminShell selected="users">
        <Alert severity="error">User not found.</Alert>
      </AdminShell>
    );
  }

  return (
    <AdminShell selected="users">
      <Box>
        <PageHeader title="Manage user" subtitle={user.email} />

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: '10px', backgroundColor: '#F7F7F7' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                User Details
              </Typography>
              <Typography>ID: {user.id}</Typography>
              <Typography>Name: {user.name}</Typography>
              <Typography>Email: {user.email}</Typography>
              <Typography>Algorithm Access: {user.algorithmAccess ? 'Enabled' : 'Disabled'}</Typography>
              <Typography>
                BAMROC License: {user.bamrocEntitlement ? `${user.bamrocEntitlement.entitlementType} (${user.bamrocEntitlement.status})` : 'Not Assigned'}
              </Typography>
              <Typography>
                License Window: {user.bamrocEntitlement ? `${formatDate(user.bamrocEntitlement.startsAtUtc)} to ${formatDate(user.bamrocEntitlement.expiresAtUtc)}` : 'N/A'}
              </Typography>
              {user.companyName && <Typography>Company: {user.companyName}</Typography>}
              <Typography>Created At: {formatDate(user.createdAt?.seconds ? new Date(user.createdAt.seconds * 1000) : user.createdAt)}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: '10px', backgroundColor: '#F7F7F7' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                BAMROC Cloud License
              </Typography>
              <TextField
                select
                label="License Type"
                fullWidth
                value={entitlementForm.entitlementType}
                onChange={(event) => handleEntitlementFieldChange('entitlementType', event.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="Trial">Trial</MenuItem>
                <MenuItem value="Full">Full</MenuItem>
              </TextField>
              <TextField
                select
                label="Status"
                fullWidth
                value={entitlementForm.status}
                onChange={(event) => handleEntitlementFieldChange('status', event.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Suspended">Suspended</MenuItem>
                <MenuItem value="Revoked">Revoked</MenuItem>
                <MenuItem value="Expired">Expired</MenuItem>
              </TextField>
              <TextField
                label="Starts At (UTC)"
                type="datetime-local"
                fullWidth
                value={entitlementForm.startsAtUtc}
                onChange={(event) => handleEntitlementFieldChange('startsAtUtc', event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Expires At (UTC)"
                type="datetime-local"
                fullWidth
                value={entitlementForm.expiresAtUtc}
                onChange={(event) => handleEntitlementFieldChange('expiresAtUtc', event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <Button variant="contained" sx={{ mr: 2 }} onClick={handleSaveEntitlement} disabled={actionLoading}>
                {actionLoading ? <CircularProgress size={20} /> : 'Save License'}
              </Button>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: '10px', backgroundColor: '#F7F7F7' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                License Status Actions
              </Typography>
              <Button variant="contained" color="success" sx={{ mr: 2, mb: 1 }} onClick={() => handleEntitlementStatusChange('Active')}>
                Set Active
              </Button>
              <Button variant="contained" color="warning" sx={{ mr: 2, mb: 1 }} onClick={() => handleEntitlementStatusChange('Suspended')}>
                Suspend
              </Button>
              <Button variant="contained" color="error" sx={{ mb: 1 }} onClick={() => handleEntitlementStatusChange('Revoked')}>
                Revoke
              </Button>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, borderRadius: '10px', backgroundColor: '#F7F7F7' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Algorithm Access
              </Typography>
              <Button variant="contained" color="success" sx={{ mr: 2 }} onClick={() => handleAlgorithmAccess(true)}>
                Enable
              </Button>
              <Button variant="contained" color="error" onClick={() => handleAlgorithmAccess(false)}>
                Disable
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: '10px', backgroundColor: '#F7F7F7' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment History
              </Typography>
              <UserPaymentHistoryView email={user.email} />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: '10px', backgroundColor: '#F7F7F7' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Project History
              </Typography>
              <UserProjectHistoryView email={user.email} />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="error"
              fullWidth
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete User
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminShell>
  );
};

export default UserManagement;
